import { RadioButton } from "primereact/radiobutton";
import styled from "styled-components";
import { BenefitServiceLine } from "types/BenefitSelection/BenefitSelection";

import { SERVICE_LINES } from "./constants";

const Row = styled.label<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 1.5rem minmax(12rem, 1fr);
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--primary-color)" : "var(--surface-200)"};
  background: ${({ $selected }) =>
    $selected ? "var(--primary-50, #f4f2ff)" : "#fff"};
  border-radius: 6px;
  cursor: pointer;

  & + & {
    margin-top: 0.35rem;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(12rem, 1fr);
  gap: 0.75rem;
  padding: 0 0.75rem 0.35rem;
  /* The app's own DataTable header: not a caption, and never lighter than the rows beneath it. */
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-label);
`;

interface IServiceLinePickerProps {
  value: BenefitServiceLine | null;
  disabled?: boolean;
  onChange: (value: BenefitServiceLine) => void;
}

/**
 * CLE-2792 — step 1: which of the payer's service lines this account's copay and co-insurance are
 * read from.
 *
 * 🚨 NO COUNTS ON THIS SCREEN, DELIBERATELY. It used to print "copay reported in 206 of 298 checks"
 * beside each line, plus an impact box comparing the selection to the saved one. Those counted how
 * often the PAYER described a service line — which says nothing about which line the ACCOUNT bills
 * under, the question this picker asks. At Merriam the highest-scoring line (physician office) is
 * the wrong answer, so the numbers read as a recommendation and recommended the wrong thing. The
 * choice is a fact about how the facility bills; the payer's own names below each label are what it
 * is checked against.
 */
export function ServiceLinePicker({
  value,
  disabled,
  onChange,
}: IServiceLinePickerProps) {
  return (
    <div
      className="flex flex-column gap-3"
      data-testid="benefitSelection.serviceLinePicker"
    >
      <div>
        <HeaderRow>
          <span />
          <span>Service line</span>
        </HeaderRow>

        {SERVICE_LINES.map((line) => (
          <Row
            key={line.value}
            $selected={value === line.value}
            htmlFor={`serviceLine-${line.value}`}
            data-testid={`benefitSelection.serviceLine.${line.value}`}
          >
            <RadioButton
              inputId={`serviceLine-${line.value}`}
              name="benefitSelectionServiceLine"
              value={line.value}
              disabled={disabled}
              checked={value === line.value}
              onChange={() => onChange(line.value)}
            />
            <span className="flex flex-column">
              <span className="font-medium text-sm">{line.label}</span>
              <span className="text-xs text-color-secondary">
                {line.pverifyNames}
              </span>
            </span>
          </Row>
        ))}
      </div>
    </div>
  );
}
